import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';

import type { SourceImageRef } from '../../../domain';
import { fileToDataUrl, isLikelyImageUrl, stripDataUrlPrefix } from './referenceUtils';

export interface ReferenceImageInputProps {
  /** Current reference value. Pass `null` for empty. */
  value: SourceImageRef | null;
  /** Fires whenever the user adds, replaces, or clears the reference. */
  onChange: (next: SourceImageRef | null) => void;
  /** Short label rendered above the slot (e.g., "Style reference"). */
  label: string;
  /** Helper text rendered when the slot is empty. */
  helperText?: string;
  /** Optional preview thumbnail URL — if `value` carries a sourceImageUrl,
   *  the component falls back to that automatically. Useful when the
   *  reference was added via base64 (no URL) and the panel wants to show
   *  the original data URL for preview. */
  previewUrl?: string;
}

/**
 * Sidecar UI for "pick a reference image". F12c's StyleTransfer / FaceSwap
 * / TryOn / etc. embed one (or two for TryOn) of these next to their
 * existing form fields. The component emits a SourceImageRef the panel's
 * builder can spread into its request DTO.
 *
 * Two ways to add a reference:
 * - File pick / drag-drop → fileToDataUrl → emit { sourceImageBase64 }
 * - URL paste → emit { sourceImageUrl }
 *
 * Validation surfaces inline (unsupported type, too large, malformed URL).
 */
export const ReferenceImageInput: React.FC<ReferenceImageInputProps> = ({
  value,
  onChange,
  label,
  helperText,
  previewUrl,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlDraft, setUrlDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDraggingOver, setDraggingOver] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      try {
        const dataUrl = await fileToDataUrl(file);
        onChange({ sourceImageBase64: stripDataUrlPrefix(dataUrl) });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to read the file.');
      }
    },
    [onChange],
  );

  const handleUrlSubmit = useCallback(() => {
    const url = urlDraft.trim();
    if (!url) {
      setError('Enter an image URL.');
      return;
    }
    if (!isLikelyImageUrl(url)) {
      setError('URL must point to an image (http(s) or data:image/…).');
      return;
    }
    setError(null);
    setUrlDraft('');
    onChange({ sourceImageUrl: url });
  }, [urlDraft, onChange]);

  const handleClear = () => {
    setError(null);
    onChange(null);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDraggingOver(true);
  };
  const onDragLeave = () => setDraggingOver(false);
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDraggingOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const displayUrl =
    previewUrl ?? value?.sourceImageUrl ?? (value?.sourceImageBase64
      ? `data:image/png;base64,${value.sourceImageBase64}`
      : null);

  return (
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {value && (
          <Tooltip title="Clear reference">
            <IconButton size="small" onClick={handleClear}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {displayUrl ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1 / 1',
            maxHeight: 144,
            borderRadius: 1,
            border: theme => `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label={`${label} preview`}
        >
          <Box
            component="img"
            src={displayUrl}
            alt={`${label} preview`}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </Box>
      ) : (
        <Box
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: theme => `1px dashed ${theme.palette.divider}`,
            borderRadius: 1,
            p: 1.5,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: isDraggingOver ? 'action.hover' : 'background.paper',
            transition: 'background-color 120ms',
          }}
          aria-label={`Choose ${label}`}
          role="button"
        >
          <AddPhotoAlternateIcon fontSize="small" sx={{ opacity: 0.7 }} />
          <Typography variant="caption" color="text.secondary" align="center">
            {helperText ?? 'Click or drag-drop an image'}
          </Typography>
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          // Reset the input so picking the same file twice re-triggers
          // onChange.
          e.target.value = '';
        }}
      />

      <Stack direction="row" spacing={0.5}>
        <TextField
          size="small"
          value={urlDraft}
          placeholder="…or paste an image URL"
          onChange={e => setUrlDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleUrlSubmit();
            }
          }}
          sx={{ flex: 1 }}
        />
        <Tooltip title="Use URL">
          <Button
            size="small"
            variant="outlined"
            onClick={handleUrlSubmit}
            disabled={urlDraft.trim().length === 0}
            sx={{ minWidth: 0, px: 1 }}
          >
            <LinkIcon fontSize="small" />
          </Button>
        </Tooltip>
      </Stack>

      {error && <Alert severity="warning">{error}</Alert>}
    </Stack>
  );
};
